import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Landmark } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/tax";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setAmount(data.amount);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (attempts >= 8) {
          setState("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= 8) setState("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="civic-hero py-6">
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-3 text-white">
          <span className="w-9 h-9 rounded-md bg-white/15 flex items-center justify-center">
            <Landmark className="w-5 h-5" />
          </span>
          <div className="leading-tight">
            <p className="font-heading font-semibold text-sm">Cheltenham Township</p>
            <p className="text-xs text-slate-200">Tax Filing Portal</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-md p-10 text-center" data-testid="payment-success-card">
          {state === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-[#1E3A8A] mx-auto animate-spin" />
              <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-6">
                Confirming your payment…
              </h1>
              <p className="text-slate-500 mt-2">This only takes a moment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <span className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </span>
              <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-6" data-testid="payment-paid-title">
                Payment received
              </h1>
              <p className="text-slate-500 mt-2">
                {amount != null ? `Your payment of ${money(amount)} was successful.` : "Your payment was successful."}{" "}
                A receipt has been recorded with your filing.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="mt-6 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full"
                data-testid="success-back-dashboard"
              >
                Back to My Filings
              </Button>
            </>
          )}
          {state === "pending" && (
            <>
              <Loader2 className="w-12 h-12 text-amber-500 mx-auto" />
              <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-6">
                Payment processing
              </h1>
              <p className="text-slate-500 mt-2">
                Your payment is still being confirmed. It may take a little longer — check your
                dashboard shortly.
              </p>
              <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full">
                Back to My Filings
              </Button>
            </>
          )}
          {state === "error" && (
            <>
              <span className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                <XCircle className="w-9 h-9 text-red-600" />
              </span>
              <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-6">
                Could not confirm payment
              </h1>
              <p className="text-slate-500 mt-2">
                We couldn't verify this payment. Please check your dashboard or try again.
              </p>
              <Link to="/dashboard">
                <Button className="mt-6 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full">
                  Back to My Filings
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
